import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { get_cabinet_plans, list_cabinet_subscriptions, create_cabinet_subscription } from '@/api/cabinet'
import type { Plan, PlanListParams } from '@/types/plan'
import type { PaymentMethod, Subscription } from '@/types/subscription'
import { Coins, Wand2, Mic, Users, HardDrive, Briefcase, Activity, Star, Package } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { WireInstructionsDialog } from '@/components/billing/wire-instructions-dialog'

export default function CabinetPlans() {
  const { t } = useTranslation()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [isChooseOpen, setChooseOpen] = useState<boolean>(false)
  const [method, setMethod] = useState<PaymentMethod>('wire')
  const [creating, setCreating] = useState<boolean>(false)
  const [instructionsOpen, setInstructionsOpen] = useState<boolean>(false)

  const params: PlanListParams = {
    ordering: 'price_monthly_cents',
    search: null,
    page_size: undefined,
    page: undefined,
    is_active: ['true'],
    is_public: ['true'],
  }

  const loadPlans = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await get_cabinet_plans(params)
      setPlans(data?.results ?? [])
    } catch (e: any) {
      console.error('Error loading plans:', e)
      setError(e?.message || 'Failed to load plans')
      setPlans([])
    } finally {
      setLoading(false)
    }
  }

  const loadSubscriptionsStatus = async () => {
    try {
      const subs = await list_cabinet_subscriptions({ page_size: 1, page: 1 })
      const has = subs.results.some((s: Subscription) => ['active', 'trialing', 'pending'].includes(s.status))
      setHasActiveSubscription(has)
    } catch (e) {
      console.warn('Unable to load subscription status:', e)
    }
  }

  useEffect(() => {
    loadPlans()
    loadSubscriptionsStatus()
  }, [])

  const priceDisplay = (cents?: number | null) => {
    if (cents == null) return '-'
    const amount = (cents / 100).toFixed(2)
    return t('price_per_month', { amount, currency: 'MAD' }) || `${amount} MAD/mo`
  }

  // Helpers to present backend features attractively
  const storageDisplay = (mb?: number | null) => {
    if (mb == null) return t('unlimited') || 'Unlimited'
    const gb = mb / 1024
    return `${gb >= 1 ? gb.toFixed(1) : mb} ${gb >= 1 ? 'GB' : 'MB'}`
  }
  const intDisplay = (n?: number | null): string => {
    // Treat -1 specifically as unlimited for numeric caps
    if (n === -1) return t('unlimited') || 'Unlimited'
    try { return n?.toLocaleString() ?? '-' } catch { return n == null ? '-' : String(n) }
  }

  const openChoose = (plan: Plan) => {
    setSelectedPlan(plan)
    setMethod('wire')
    setChooseOpen(true)
  }

  const confirmChoose = async () => {
    if (!selectedPlan || selectedPlan.id == null) return
    setCreating(true)
    try {
      await create_cabinet_subscription({
        plan: Number(selectedPlan.id),
        method,
        currency: 'MAD',
        cancel_at_period_end: false,
      })
      // Wire payments are validated manually: show where to send the transfer
      setChooseOpen(false)
      setInstructionsOpen(true)
      await loadSubscriptionsStatus()
    } catch (e: any) {
      setError(e?.message || 'Failed to create subscription')
    } finally {
      setCreating(false)
    }
  }

  return (
    <>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Package className="h-5 w-5" />
            </span>
          }
          title={t('plans') || 'Plans'}
          description={t('cabinet_plans_desc') || 'Choose a subscription plan for your cabinet.'}
        />

        {hasActiveSubscription && (
          <div className='mb-4'>
            <Badge variant='secondary'>{t('already_subscribed') || 'Already subscribed'}</Badge>
          </div>
        )}

        {loading ? (
          <div className='py-8 text-center'>{t('loading') || 'Loading'}...</div>
        ) : error ? (
          <div className='py-8 text-center'>{error}</div>
        ) : plans.length === 0 ? (
          <div className='py-8 text-center text-muted-foreground'>
            {t('no_plans_available') || 'No plans available'}
          </div>
        ) : (
          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            {plans.map((plan) => {
              const isFeatured = !!plan.is_recommended
              const isFree = (plan.price_monthly_cents ?? 0) === 0
              const monthly = plan.price_monthly_cents ?? null
              const yearly = plan.price_yearly_cents ?? null
              const savingsPct = monthly && yearly ? Math.max(0, Math.round(100 * (1 - (yearly / (monthly * 12))))) : null

              // New logic: AI assistance derives from monthly_token_cap
              const aiTokens = plan.monthly_token_cap
              const aiEnabled = aiTokens != null && aiTokens !== 0

              // New logic: audio access visibility derives from audio_access_enabled
              const audioCap = plan.audio_access_enabled
              const showAudio = audioCap != null && audioCap !== 0

              return (
                <Card key={plan.code} className={`overflow-hidden transition shadow-sm hover:shadow-md ${isFeatured ? 'ring-1 ring-amber-300' : ''}`}>
                  <CardContent className='p-6'>
                    <div className='mb-3 flex items-start justify-between'>
                      <div>
                        <h3 className='text-lg font-semibold'>{plan.name}</h3>
                        <p className='text-sm text-muted-foreground'>{plan.description || '-'}</p>
                      </div>
                      {isFeatured && (
                        <Badge variant='success' className='flex items-center gap-1'>
                          <Star className='h-3.5 w-3.5 text-amber-600' />
                          {t('recommended') || 'Recommended'}
                        </Badge>
                      )}
                      {plan.is_current && (
                        <Badge variant='secondary' className='ms-2'>
                          {t('current_plan') || 'Current plan'}
                        </Badge>
                      )}
                    </div>

                    <div className='mt-2 mb-4 flex items-baseline gap-2'>
                      <Coins className='h-5 w-5 text-amber-600' />
                      <div className='text-2xl font-bold'>
                        {isFree ? (t('free') || 'Free') : priceDisplay(plan.price_monthly_cents)}
                      </div>
                      {(plan.trial_days ?? 0) > 0 && (
                        <span className='ms-auto text-xs rounded bg-amber-50 px-2 py-1 text-amber-700'>
                          {(plan.trial_days ?? 0)} {t('trial_days') || 'trial days'}
                        </span>
                      )}
                      {yearly != null && (
                        <span className='text-xs text-muted-foreground ms-auto'>
                          {t('or') || 'or'} {t('price_per_year', { amount: (yearly / 100).toFixed(2), currency: 'MAD' }) || `${(yearly / 100).toFixed(2)} MAD/year`}
                          {savingsPct && savingsPct > 0 ? ` • ${t('save_percent', { pct: savingsPct }) || `Save ${savingsPct}%`}` : ''}
                        </span>
                      )}
                    </div>

                    <div className='mt-3 grid grid-cols-1 gap-2 text-sm'>
                      <div className='flex items-center gap-2'>
                        <Wand2 className={`h-4 w-4 ${aiEnabled ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                        <span>{t('ai_assistance') || 'AI Assistance'}</span>
                        <span className='ms-auto text-muted-foreground'>
                          {aiEnabled ? (t('included') || 'Included') : (t('not_included') || 'Not included')}
                        </span>
                      </div>

                      {showAudio && (
                        <div className='flex items-center gap-2'>
                          <Mic className='h-4 w-4 text-emerald-600' />
                          <span>{t('audio_access') || 'Audio Access'}</span>
                          <span className='ms-auto text-muted-foreground'>
                            {intDisplay(audioCap)}
                          </span>
                        </div>
                      )}

                      <div className='flex items-center gap-2'>
                        <Users className='h-4 w-4 text-sky-600' />
                        <span>{t('max_users') || 'Max users'}</span>
                        <span className='ms-auto text-muted-foreground'>{intDisplay(plan.max_users)}</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Wand2 className='h-4 w-4 text-violet-600' />
                        <span>{t('monthly_ai_tokens') || 'Monthly AI tokens'}</span>
                        <span className='ms-auto text-muted-foreground'>{intDisplay(plan.monthly_token_cap)}</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <HardDrive className='h-4 w-4 text-rose-600' />
                        <span>{t('storage') || 'Storage'}</span>
                        <span className='ms-auto text-muted-foreground'>{storageDisplay(plan.max_storage_mb)}</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Briefcase className='h-4 w-4 text-amber-600' />
                        <span>{t('max_cases') || 'Max cases'}</span>
                        <span className='ms-auto text-muted-foreground'>{intDisplay(plan.max_cases)}</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Activity className='h-4 w-4 text-emerald-600' />
                        <span>{t('max_active_cases') || 'Max active cases'}</span>
                        <span className='ms-auto text-muted-foreground'>{intDisplay(plan.max_active_cases)}</span>
                      </div>
                      {plan.extras && (
                        <div className='mt-2 flex flex-wrap gap-2'>
                          {Object.entries((plan.extras as Record<string, unknown>) || {}).map(([key, value]) => (
                            <Badge key={key} variant='outline' className='border-muted-foreground/30'>
                              {key}: {String(value)}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className='mt-4 flex gap-3'>
                        <Button variant={isFree ? 'secondary' : 'default'} className='ms-auto' onClick={() => openChoose(plan)} disabled={creating}>
                          {plan.is_current ? (t('current_plan') || 'Current plan') : (t('choose_plan') || 'Choose plan')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        <Dialog open={isChooseOpen} onOpenChange={setChooseOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('choose_plan') || 'Choose plan'}</DialogTitle>
            </DialogHeader>
            <div>
              <p className='text-sm text-muted-foreground'>{t('confirm_choose_plan') || 'Confirm plan selection and payment method.'}</p>
            </div>
            <div className='mt-3'>
              <Label className='text-sm'>{t('payment_method') || 'Payment method'}</Label>
              <RadioGroup value={method} onValueChange={(v) => setMethod(v as PaymentMethod)} className='mt-2'>
                <div className='flex items-center gap-2'>
                  <RadioGroupItem value='wire' id='wire' />
                  <Label htmlFor='wire'>{t('bank_transfer') || 'Bank transfer'}</Label>
                </div>
              </RadioGroup>
            </div>
            <DialogFooter>
              <Button variant='secondary' onClick={() => setChooseOpen(false)} disabled={creating}>{t('cancel') || 'Cancel'}</Button>
              <Button onClick={confirmChoose} disabled={creating}>{t('confirm') || 'Confirm'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <WireInstructionsDialog open={instructionsOpen} onOpenChange={setInstructionsOpen} />
      </Main>
    </>
  )
}